// Libs
import classNames from 'classnames/bind';
import { Box } from '@mui/material';
import moment, { unitOfTime } from 'moment';
import { memo, useEffect, useState } from 'react';
import Timeline, {
  CustomHeader,
  TimelineGroupBase,
  TimelineHeaders,
  TimelineItemBase,
} from 'react-calendar-timeline';
import { ReactCalendarItemRendererProps } from 'react-calendar-timeline';
// Components, Layouts, Pages
// Others
import { DateFormatEnum } from '~/utils/enum/common';
// Styles, images, icons
import 'react-calendar-timeline/dist/Timeline.scss';
import styles from './BaseTimelineSchedule.module.scss';

export type BaseTimelineProps<T> = {
  currentTime?: Date | moment.Moment;
  items: Array<BaseTimelineDayItem<T>>;
  groups: Array<TimelineGroupBase>;
  unit?: string;
  itemRenderer?: (item: BaseTimelineDayItem<T>) => React.ReactNode;
};

const cx = classNames.bind(styles);

export type BaseTimelineDayItem<TItem> = {
  resource: Array<TItem>;
} & TimelineItemBase<Date>;

const BaseTimelineSchedule = <T extends Record<any, any>>(props: BaseTimelineProps<T>) => {
  //#region Destructuring Props
  const { currentTime = moment(), unit = 'month' } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [startTime, setStartTime] = useState<Date>();
  const [endTime, setEndTime] = useState<Date>();
  const [items, setItems] = useState<Array<BaseTimelineDayItem<T>>>([]);
  const [key, setKey] = useState<number>();
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    setItems(props.items as any as Array<BaseTimelineDayItem<T>>);
    setKey(Date.now());
  }, [props.items]);

  useEffect(() => {
    setStartTime(
      moment(currentTime)
        .startOf(unit as unitOfTime.StartOf)
        .toDate()
    );
    setEndTime(
      moment(currentTime)
        .endOf(unit as unitOfTime.StartOf)
        .toDate()
    );
  }, [currentTime, unit]);
  //#endregion Implement Hook

  //#region Handle Function
  const itemRenderer = (calendarProps: ReactCalendarItemRendererProps<BaseTimelineDayItem<T>>) => {
    return (
      <Box {...calendarProps.getItemProps({})} key={calendarProps?.item?.id}>
        {props?.itemRenderer
          ? props?.itemRenderer(calendarProps.item)
          : calendarProps?.item?.resource.map(({ title }) => title).join(', ')}
      </Box>
    );
  };
  //#endregion Handle Function

  return (
    startTime &&
    endTime && (
      <Timeline
        groups={props.groups}
        items={items}
        defaultTimeStart={startTime}
        defaultTimeEnd={endTime}
        visibleTimeStart={startTime.valueOf()}
        visibleTimeEnd={endTime.valueOf()}
        sidebarWidth={0}
        className={cx('base-timeline')}
        key={key}
        itemRenderer={itemRenderer}
      >
        <TimelineHeaders>
          <CustomHeader>
            {(options: any) => {
              return (
                <Box {...options.getRootProps()}>
                  {options.headerContext.intervals.map((interval: any) => {
                    return (
                      <Box
                        className='rct-dateHeader'
                        {...options.getIntervalProps({
                          interval,
                        })}
                        key={interval.startTime.format(DateFormatEnum.YYYY_MM_DD)}
                      >
                        <Box className='flex flex-row'>
                          <Box className='text-[#BCB8DC] mr-[4px]'>
                            {interval.startTime.format('dd').substring(0, 1)}
                          </Box>
                          <Box className='text-[#124378]'>{interval.startTime.format('DD')}</Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            }}
          </CustomHeader>
        </TimelineHeaders>
      </Timeline>
    )
  );
};

export default memo(BaseTimelineSchedule) as typeof BaseTimelineSchedule;
